import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { BodyPrimary, H1, H2, H3 } from '@liveauctioneers/hammer-ui-core/text';
import styled from 'styled-components';

import PageTitle from '@/components/PageTitle/PageTitle';

import PrivacyPageHeaderMeta from './PrivacyPageHeaderMeta';

const PrivacyNotice = () => {
    return (
        <PrivacyContainer>
            <PrivacyPageHeaderMeta />
            <PageTitle title={<FormattedMessage id="privacyPolicy.title" />} />
            <Title>Privacy Notice</Title>
            <Paragraph>
                This privacy notice explains how Auction Technology Group (“ATG”) companies use personal data across our
                companies and the following websites:
            </Paragraph>
            <List>
                <ListItem>
                    AntiquesTradeGazette.com operated by Metropress Ltd incorporated in England and Wales
                </ListItem>
                <ListItem>AuctionMobility.com operated by Auction Mobility LLC incorporated in Delaware USA</ListItem>
                <ListItem>
                    AuctionTechnologyGroup.com operated by Metropress Ltd incorporated in England and Wales
                </ListItem>
                <ListItem>
                    BidSpotter.co.uk in the UK operated by Metropress Ltd incorporated in England and Wales
                </ListItem>
                <ListItem>BidSpotter.com operated by Proxibid Inc incorporated in Delaware USA</ListItem>
                <ListItem>i-Bidder.com in the UK operated by Metropress Ltd incorporated in England and Wales</ListItem>
                <ListItem>Jasper52 operated by Live Auctioneers LLC incorporated in New York USA</ListItem>
                <ListItem>Liveauctioneers.com operated by Live Auctioneers LLC incorporated in New York USA</ListItem>
                <ListItem>Proxibid.com operated by Proxibid Inc. incorporated in Delaware USA</ListItem>
                <ListItem>
                    the-saleroom.com in the UK operated by Metropress Ltd incorporated in England and Wales
                </ListItem>
                <ListItem>Wavebid.com operated by Proxibid Inc incorporated in Delaware USA</ListItem>
            </List>
            <Paragraph>
                By creating an account on any of the websites listed above you agree to be bound by the terms and
                conditions of this Privacy Policy. If you do not agree to its terms and conditions you should not use or
                access our websites. By accepting the Privacy Policy you expressly consent to our use and disclosure of
                your personal data in accordance with the Policy.
            </Paragraph>
            <Paragraph>
                Auction Technology Group Germany GmbH operates as our European representative under EU GDPR.
            </Paragraph>
            <SectionTitle>How to contact us</SectionTitle>
            <Paragraph>
                We are here to help you with any questions or concerns you may have about our use of your personal data.
                If you require further information, wish to make a request or exercise your rights under applicable laws
                you can contact us as follows:
            </Paragraph>
            <Paragraph>
                <strong>Via the website</strong>
            </Paragraph>
            <Paragraph>Via the contact us forms on the websites.</Paragraph>
            <Paragraph>Via the messaging service on your account.</Paragraph>
            <Paragraph>
                <strong>Via phone</strong>
            </Paragraph>
            <Paragraph>
                US:
                <List>
                    <ListItem>BidSpotter.com: +1 866-597-2437</ListItem>
                    <ListItem>LiveAuctioneers: +1 888-600-2437 ext 1</ListItem>
                    <ListItem>Proxibid: +1 877-505-7770</ListItem>
                    <ListItem>Wavebid: +1 833-616-7370</ListItem>
                </List>
            </Paragraph>
            <Paragraph>Germany: +49 (0)40 303731800</Paragraph>
            <Paragraph>
                UK:
                <List>
                    <ListItem>ATG: +44 (0) 203 725 5500</ListItem>
                    <ListItem>Antiques Trade Gazette: +44 (0) 20 3725 5507</ListItem>
                </List>
            </Paragraph>
            <Paragraph>
                <strong>Via post</strong>
                <List>
                    <ListItem>USA - Auction Technology Group 10 E 38th St Floor 4 New York NY 10016 USA</ListItem>
                    <ListItem>
                        Germany (EU Representative Office) - Auction Technology Group Germany GmbH Große Bäckerstraße 9
                        20095 Hamburg Germany
                    </ListItem>
                    <ListItem>UK - Auction Technology Group 65 Southwark Street London SE1 0HR UK</ListItem>
                </List>
            </Paragraph>
            <Paragraph>
                To escalate a matter to our independent Group Data Protection Office contact the DPO via:
                <List>
                    <ListItem>the contact us or support pages on our websites or</ListItem>
                    <ListItem>email at DPO@AuctionTechnologyGroup.com</ListItem>
                </List>
            </Paragraph>
            <SectionTitle>How do we use personal data?</SectionTitle>
            <Paragraph>Our use of personal data depends on which services you are using as outlined below:</Paragraph>
            <SubSectionTitle>If you visit our websites</SubSectionTitle>
            <Paragraph>
                If you visit our websites we will process details of your IT device (such as your computer, phone,
                tablet, or other device) and website usage to help manage website security and website performance as a
                reasonable and legitimate business interest.
            </Paragraph>
            <Paragraph>
                Our websites contain links to and from other external websites such as those of auctioneers, our partner
                networks, advertisers, and affiliates. If you follow a link to any external websites, please note that
                these websites have their own processes and privacy notices and that we do not accept any responsibility
                or liability for these notices, for data you give to those organisations, nor for their processing.
                Please check their notices before you share any personal data with those websites or organisations.
            </Paragraph>
            <Paragraph>
                Our websites also use cookies to store data on your IT device related to your use of our websites.
                Further details can be found on our Cookie Notice page which can be accessed via the link at the bottom
                of our webpages. If you have a Facebook account we will share your ATG account flag so that Facebook can
                send you targeted marketing but only if you have opted in to this via your Facebook account. Please
                contact us if you want to opt out using the details above.
            </Paragraph>
            <SubSectionTitle>If you contact us</SubSectionTitle>
            <Paragraph>
                We will use the information you provide including your user details and contact details to discuss the
                matter you have raised as a reasonable and legitimate business interest.
            </Paragraph>
            <SubSectionTitle>If you subscribe to our communications</SubSectionTitle>
            <Paragraph>
                If you subscribe to our newsletters, promotional communications, and updates, we will process your user
                and contact details to provide the requested information as a reasonable and legitimate business
                interest. You can unsubscribe from those communications at any time by:
                <List>
                    <ListItem>Contacting us via contact details provided on this website;</ListItem>
                    <ListItem>Using the “unsubscribe” link in emails or “STOP” number in texts; or</ListItem>
                    <ListItem>Updating your marketing preferences via the preference centre for your account.</ListItem>
                </List>
            </Paragraph>
            <SubSectionTitle>
                If you participate in a survey, competition, loyalty reward program, webcast, or promotion
            </SubSectionTitle>
            <Paragraph>
                We will process the data you provide for that specific activity including user and contact details. This
                may include postal, telephone, email, SMS, or social media communications related to that activity.
            </Paragraph>
            <Paragraph>
                Some activities will also provide the option to opt-in to marketing updates and offers if you provide
                your consent.
            </Paragraph>
            <SubSectionTitle>If you join our auction community by registering on our site</SubSectionTitle>
            <Paragraph>
                If you register on our website, we will process the personal data that is necessary for us to provide
                our services to you including sign-up identification, service updates, product updates, bidding,
                winning, payment, any payment defaults, shipping, feedback, legal compliance, and bidder support under
                our contractual obligations.
            </Paragraph>
            <Paragraph>
                Over time, as necessary for the fulfilment of the contract, personal data will include user contact
                details, IT device, website usage, bid, purchase, risk, and payment histories.
            </Paragraph>
            <Paragraph>
                We may send newsletters, a timely reminder of lots, watchlists, or auctions for which you have
                registered via SMS, in-app notifications, or email as a reasonable and legitimate business interest so
                you are less likely to miss an auction. You can opt out from receiving these messages any time.
            </Paragraph>
            <Paragraph>
                We may perform risk-based analysis of bidder data for fraud and crime prevention purposes as a public
                interest and to determine your eligibility to bid during sign-up and once you have become a registered
                bidder. We may notify our business partners such as auctioneers of potential high-risk activities to
                help protect our community by detecting, investigating, and preventing unusual account activities.
            </Paragraph>
            <Paragraph>
                We contact bidders directly via email with communications that are necessary for contractual or legal
                reasons such as operational issues, delivery information, or payment updates.
            </Paragraph>
            <Paragraph>
                Adverts that have been specifically selected for you will also be presented on other websites such as
                social media websites that you visit as a legitimate business interest. Those websites use their cookies
                (with your consent) or one-way-encrypted (hashed) email address to identify your account. Those websites
                will not have access to your auctioneer or bidder account or bidding data. You can opt out of this by
                changing your account setting or preferences on these websites.
            </Paragraph>
            <Paragraph>
                We also anonymise user data so that we can anonymously analyse aggregated customer trends and business
                performance over time as a legitimate business interest.
            </Paragraph>
            <SubSectionTitle>Bidding</SubSectionTitle>
            <Paragraph>
                If you register to bid in an auction via one of our websites we will share your contact details and bid,
                purchase, and payment histories via our website with the relevant auctioneer to enable the auctioneer to
                determine whether to approve you to bid in their auction.
            </Paragraph>
            <SubSectionTitle>Winning Bids</SubSectionTitle>
            <Paragraph>
                If you win a lot as a result of a bid placed via our website we will share your contact details,
                purchase, and payment details (using a tokenised version of your payment card) with the auctioneer for
                that auction for them to fulfil the order. That auctioneer is responsible for confirming your winning
                bid and delivering the goods you have purchased from them. Where we are shipping the lot we will also
                process your details as necessary for the delivery including the necessary sharing of your data with
                shipping companies.
            </Paragraph>
            <Paragraph>
                Those auctioneers and shipping companies will be operating as independent data controllers for the
                purpose of processing that data. We have no control or liability for the use of data by those companies
                and their own policies and notices apply.
            </Paragraph>
            <Paragraph>
                Any issues with the goods you have purchased must be raised directly with the auctioneer.
            </Paragraph>
            <SubSectionTitle>Payments</SubSectionTitle>
            <Paragraph>
                Where financial transactions require bank account details to be processed we will only use those details
                for the purpose of agreed payments.
            </Paragraph>
            <Paragraph>
                Payment cards services for bidders are provided by specialist third parties such as Payrix, Plug'n Pay
                Technologies, and Global Payments who operate as independent data controllers. Their policies, notices,
                and controls apply. We will retain partial information such as a “card transaction token”.
            </Paragraph>
            <Paragraph>
                We store your Bank Account Information (including the name, routing number, account number, and current
                balance of your account at a financial institution from which you may make payments). We use Plaid to
                authenticate information regarding your account. We may use this information to execute transactions and
                debit your account.
            </Paragraph>
            <SubSectionTitle>Protecting Our Auction Community</SubSectionTitle>
            <Paragraph>
                We take reasonable proactive steps to help protect all parties that we work with from illegal, unfair,
                or inappropriate behaviours as a reasonable public interest.
            </Paragraph>
            <Paragraph>
                We will monitor positive and negative account activities including user contact details, IT device,
                website usage, bid, purchase, and payment histories. We share such information with partners such as
                auctioneers and relevant third-party service providers to help all parties manage their commercial risks
                and prevent fraud under our collaborative auction service.
            </Paragraph>
            <Paragraph>
                Accounts and services may be suspended, blocked, or closed where behaviours are thought by us in our
                sole discretion to be inappropriate or create a commercial risk. You will be notified directly of any
                such action if applicable to you. To dispute an account action contact our customer services team.
            </Paragraph>
            <Paragraph>
                We reserve the right to take legal or debt collection actions against organisations or individuals where
                relevant and will share data with necessary parties such as law enforcement, debt collection agencies,
                and credit bureaus in order to do so.
            </Paragraph>
            <SubSectionTitle>White Labelled Auction Platforms</SubSectionTitle>
            <Paragraph>
                Where we are providing technological services to an auctioneer such that you are the direct customer of
                the auctioneer, the auctioneer is the data controller and we are their data processor for this
                processing. The auctioneer is responsible for the use of your data. Any requests you wish to raise must
                be raised directly with the auctioneer for that auction.
            </Paragraph>
            <SubSectionTitle>Engaging as an Auctioneer or Seller</SubSectionTitle>
            <Paragraph>
                We will process contact details of the people working for auctioneer in order to professionally engage
                with your organisations for business-to-business purposes as a legitimate business interest.
            </Paragraph>
            <SubSectionTitle>Journalism</SubSectionTitle>
            <Paragraph>
                We will publish information that we believe to be newsworthy in relation to our business or auction
                industry as a public interest. This can include names, locations, companies, and auction-related
                information.
            </Paragraph>
            <SectionTitle>Your rights</SectionTitle>
            <Paragraph>
                We respect your right to not be discriminated against by us because you exercised any of your rights
                under applicable data protection legislation. This means we will not among other things on the basis of
                your data subject request:
            </Paragraph>
            <List>
                <ListItem>Deny goods or services to you;</ListItem>
                <ListItem>
                    Charge different prices or rates for goods or services including through the use of discounts or
                    other benefits or imposing penalties;
                </ListItem>
                <ListItem>Provide a different level or quality of goods or services to you; or</ListItem>
                <ListItem>
                    Suggest that you will receive a different price or rate for goods or services or a different level
                    or quality of goods or services.
                </ListItem>
            </List>
            <SubSectionTitle>
                For residents of the UK and EU - Your Legal Rights Under the General Data Protection Regulation
            </SubSectionTitle>
            <Paragraph>If we are processing your personal data you have the right to ask that we:</Paragraph>
            <List>
                <ListItem>
                    Tell you how your data is being used including how data is processed by third parties on our behalf;
                </ListItem>
                <ListItem>Send you or your nominated third party a copy of your data;</ListItem>
                <ListItem>
                    Stop or suspend processing of your data or opt out of consent that you have provided including our
                    use of data for marketing;
                </ListItem>
                <ListItem>Correct or erase your data; and/or</ListItem>
                <ListItem>
                    Explain and manually review any automated decision making or profiling of your data.
                </ListItem>
            </List>
            <SubSectionTitle>
                For Residents of Certain US states - Your Legal Rights Under the California Consumer Privacy Act of 2018
                (“CCPA”) and other state privacy laws
            </SubSectionTitle>
            <Paragraph>
                If you are a California resident you have certain rights regarding your personal data (referred to as
                “personal information”) under the CCPA including the right to request that we delete or correct personal
                information that we have collected from you; the right to opt-out of the sale or sharing of your
                personal information with our service providers; the right to limit our use of your sensitive personal
                information; and the right not to be discriminated against for exercising your rights. These rights are
                subject to certain exceptions.
            </Paragraph>
            <Paragraph>
                Under California laws you or your authorized agent have the right to know what ATG collects or discloses
                about you in the last 12 months related to your account:
            </Paragraph>
            <List>
                <ListItem>The categories of personal information we have collected.</ListItem>
                <ListItem>The categories of sources from which the personal information is collected.</ListItem>
                <ListItem>The business or commercial purpose for collecting or selling personal information.</ListItem>
                <ListItem>The categories of third parties with whom we share personal information.</ListItem>
                <ListItem>The specific pieces of personal information we have collected about you.</ListItem>
            </List>
            <Paragraph>
                If you would like to exercise any of the privacy rights provided to you under your applicable state law
                please refer to the “How to Contact Us” section of this policy.
            </Paragraph>
            <SubSectionTitle>Responding to your requests</SubSectionTitle>
            <Paragraph>
                Before responding we will need to take steps to verify your identity for reasons of security (e.g.
                confirming your information we have on file). Once your identity is verified responses will typically be
                completed within one month and at no cost to you.
            </Paragraph>
            <Paragraph>
                We will endeavour to fulfil your request but may be subject to limitations in our responses for legal or
                contractual reasons. For example we will not delete data if there is a legal or contractual obligation
                for us to retain that data. We will provide specific details of these limitations as relevant to your
                request if and when applicable.
            </Paragraph>
            <Paragraph>
                To exercise any of the above listed rights please contact our Customer Service teams using the contact
                details provided on this website highlighting that you are raising a Data Subject Request.
            </Paragraph>
            <Paragraph>
                If you are not happy with the way we have handled your data you have the right to escalate the matter to
                our Group independent Data Protection Office (
                <Link href="mailto:DPO@AuctionTechnologyGroup.com">DPO@AuctionTechnologyGroup.com</Link>) to our
                supervisory authority (the Information Commissioner's Office at ico.org.uk) or to initiate legal action.
                However if you ever have any problems we ask that you contact our Customer Service teams first so that
                we can help to resolve the issue directly using the contact details provided above.
            </Paragraph>
            <SubSectionTitle>Data Retention</SubSectionTitle>
            <Paragraph>
                We retain data for as long as we believe is required to fulfil the purpose that the data was collected
                for or to address potential associated legal or operational matters.
            </Paragraph>
            <SubSectionTitle>Categories of Personal Data</SubSectionTitle>
            <Paragraph>
                Where categories of personal data are referenced the following types of personal data are included.
            </Paragraph>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableHeader>Categories of Personal Data</TableHeader>
                        <TableHeader>Specific Types of Personal Data Collected</TableHeader>
                    </TableRow>
                </TableHead>
                <TableBody>
                    <TableRow>
                        <TableData>User details</TableData>
                        <TableData>
                            <List>
                                <ListItem>Name (First, Last)</ListItem>
                                <ListItem>Username and Password</ListItem>
                                <ListItem>
                                    Passport Number and Issuing Country (Verification of auctioneer business owners)
                                </ListItem>
                                <ListItem>Communication preferences (e.g. marketing)</ListItem>
                                <ListItem>Complaints, issues, queries, comments, feedback</ListItem>
                                <ListItem>Event participation flag (e.g. surveys or competitions)</ListItem>
                                <ListItem>
                                    Risk indicators (e.g. feedback, blocks or bans initiated by ourselves or
                                    auctioneers, payment issues, defaults, bidding issues, device issues, location
                                    issues, etc)
                                </ListItem>
                            </List>
                        </TableData>
                    </TableRow>
                    <TableRow>
                        <TableData>Contact details</TableData>
                        <TableData>
                            <List>
                                <ListItem>Postal address</ListItem>
                                <ListItem>Phone number</ListItem>
                                <ListItem>Cell phone number</ListItem>
                                <ListItem>Email address</ListItem>
                            </List>
                        </TableData>
                    </TableRow>
                    <TableRow>
                        <TableData>IT Device details</TableData>
                        <TableData>
                            <List>
                                <ListItem>IP Address</ListItem>
                                <ListItem>Login credentials</ListItem>
                                <ListItem>Type of operating system and browser</ListItem>
                                <ListItem>Type of device and operating system</ListItem>
                                <ListItem>Geographical location</ListItem>
                                <ListItem>Preferred language</ListItem>
                            </List>
                        </TableData>
                    </TableRow>
                    <TableRow>
                        <TableData>Website usage details</TableData>
                        <TableData>
                            <List>
                                <ListItem>Pages viewed</ListItem>
                                <ListItem>Links clicked (webpages and emails)</ListItem>
                                <ListItem>Date and time of visit</ListItem>
                                <ListItem>Webpage performance errors and debugging data</ListItem>
                                <ListItem>Duration of visit</ListItem>
                            </List>
                        </TableData>
                    </TableRow>
                    <TableRow>
                        <TableData>Bid details</TableData>
                        <TableData>
                            <List>
                                <ListItem>Search criteria</ListItem>
                                <ListItem>Items viewed</ListItem>
                                <ListItem>Auctions Registered</ListItem>
                                <ListItem>Items bid on, amount, date, and time.</ListItem>
                            </List>
                        </TableData>
                    </TableRow>
                    <TableRow>
                        <TableData>Purchase details</TableData>
                        <TableData>
                            <List>
                                <ListItem>Product</ListItem>
                                <ListItem>Date and time of winning bid</ListItem>
                                <ListItem>Amount of winning bid</ListItem>
                                <ListItem>Delivery address</ListItem>
                                <ListItem>Auctioneer comments and feedback</ListItem>
                            </List>
                        </TableData>
                    </TableRow>
                    <TableRow>
                        <TableData>Payment details</TableData>
                        <TableData>
                            <List>
                                <ListItem>Bank account information (routing number and account number)</ListItem>
                                <ListItem>Payment card information stored as a card transaction token</ListItem>
                                <ListItem>Timing and status of payments</ListItem>
                            </List>
                        </TableData>
                    </TableRow>
                </TableBody>
            </Table>
            <Paragraph>
                We do not collect the following GDPR Special Categories of personal data about you regarding race or
                ethnicity, political opinions, religious or philosophical beliefs, trade union membership, genetics,
                biometrics, health, sex life, or sexual orientation. We also do not collect data about criminal
                convictions or offenses. We request that you refrain from providing such data. We may collect certain
                data about you that may be considered sensitive personal data in some jurisdictions for purposes
                described above.
            </Paragraph>
            <SubSectionTitle>Other Matters</SubSectionTitle>
            <Paragraph>
                We routinely transfer personal information to external processing organisations (Processors) who help us
                provide our services under written contractual agreement such that they are only authorised to use the
                information for our purposes. External organisations include:
            </Paragraph>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableHeader>Third party service providers</TableHeader>
                        <TableHeader>What information we share</TableHeader>
                    </TableRow>
                </TableHead>
                <TableBody>
                    <TableRow>
                        <TableData>
                            <List>
                                <ListItem>Analytics providers</ListItem>
                                <ListItem>ATG companies and auditors</ListItem>
                                <ListItem>
                                    Business service providers including insurance, IT, finance, marketing, operational,
                                    and security support
                                </ListItem>
                                <ListItem>Credit reference agencies</ListItem>
                                <ListItem>Law enforcement agencies</ListItem>
                                <ListItem>Marketing partners and social media sites</ListItem>
                                <ListItem>Professional service providers</ListItem>
                                <ListItem>Research companies</ListItem>
                            </List>
                        </TableData>
                        <TableData>
                            <List>
                                <ListItem>Analytics information</ListItem>
                                <ListItem>Auctioneer feedback</ListItem>
                                <ListItem>Bidder account information</ListItem>
                                <ListItem>Customer support information</ListItem>
                                <ListItem>Survey responses</ListItem>
                                <ListItem>Transaction details</ListItem>
                            </List>
                        </TableData>
                    </TableRow>
                </TableBody>
            </Table>
            <Paragraph>
                We do not sell personal information or aggregated personal information. We do not permit use of our
                services by children under the age of 16. We do aggregate personal data for the purpose of internal
                management reporting.
            </Paragraph>
            <br />
            <br />
            <Paragraph>
                If you do not provide personal information we ask for it may delay or prevent us from providing
                associated services to you.
            </Paragraph>
            <br />
            <br />
            <Paragraph>
                We only collect personal data directly from the data subject via our electronic platforms. We do not
                purchase personal data from third parties.
            </Paragraph>
            <br />
            <br />
            <Paragraph>
                We will share personal data with governmental or law enforcement authorities where required to do so to
                comply with legal obligations or to prevent, detect, investigate, or prosecute crimes, losses, or
                damages.
            </Paragraph>
            <br />
            <br />
            <Paragraph>
                We may also need to share your data with other parties such as potential buyers of some or all of our
                business or during a re-structuring. We will typically anonymize information but this may not always be
                possible. The recipient of the information will be bound by confidentiality obligations. If your data is
                transferred to a third-party organisation as a result of corporate merger or acquisition activities this
                privacy notice will still apply unless a privacy notice is provided to you by the third party.
            </Paragraph>
            <br />
            <br />
            <Paragraph>
                We engage third party processors, including the other ATG entities under contractual agreement to
                process data on our behalf.
            </Paragraph>
            <br />
            <br />
            <Paragraph>
                Other than stated in this Privacy Notice we will not share, trade, sell, or rent data to other third
                parties. We do not allow our third-party service providers to use your personal data for their own
                purposes.
            </Paragraph>
            <br />
            <br />
            <Paragraph>
                Where data is transferred internationally, appropriate safeguards will be applied such as transfers to
                countries approved as having adequate data protection legislation, necessity for contract, approved
                contractual clauses, international data transfer agreements, or US data bridge registrations.
            </Paragraph>
            <br />
            <br />
            <Paragraph>
                We use extensive operational, technical, physical, and organisational measures to help protect personal
                data from unauthorised access, loss, unlawful processing, damage, or destruction.
            </Paragraph>
            <br />
            <br />
            <Paragraph>
                You should only share payments data via secure portals which are directly processed by our payment
                providers as independent data controllers.
            </Paragraph>
            <br />
            <br />
            <Paragraph>
                This privacy notice has been written with brevity and usability in mind to help you easily understand
                how we use personal data. If you have any further questions or comments or would like more detail,
                please contact our Customer Services team directly.
            </Paragraph>
            <br />
            <br />
            <Paragraph>
                We reserve the right to update this information at any time. If we make material changes to this privacy
                notice we will take reasonable steps to notify affected data subjects.
            </Paragraph>
            <br />
            <br />
            <Paragraph>This privacy notice was last updated July 2024.</Paragraph>
        </PrivacyContainer>
    );
};

const PrivacyContainer = styled.div`
    width: 100%;
    margin: 0 auto;
    padding: 0 20px 30px;

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        max-width: 100%;
    }

    @media (min-width: 1300px) {
        max-width: ${({ theme }) => theme.breakpoints.pageWidth};
    }
`;

const Title = styled(H1)`
    text-align: center;
    margin-bottom: 20px;
`;

const SectionTitle = styled(H2)`
    margin-top: 20px;
    margin-bottom: 10px;
`;

const SubSectionTitle = styled(H3)`
    margin-top: 15px;
    margin-bottom: 5px;
`;

const Paragraph = styled(BodyPrimary)`
    margin-bottom: 15px;
`;

const List = styled.ul`
    list-style-type: disc;
    margin-left: 20px;
    margin-bottom: 15px;
`;

const ListItem = styled.li`
    margin-bottom: 5px;
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 20px;
`;

const TableHead = styled.thead``;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
    border-bottom: 1px solid #ddd;
`;

const TableHeader = styled.th`
    padding: 10px;
    text-align: left;
    font-weight: bold;
`;

const TableData = styled.td`
    padding: 10px;
`;

export default PrivacyNotice;
